import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ExportProfile } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useRpc } from '../data/people.ts'
import { postAdminDownload, saveBlob } from '../lib/adminApi.ts'
import { fmtNum } from '../lib/format.ts'
import { sizeText } from '../data/documents.ts'
import { formatLocal } from '../lib/time.ts'
import { BackLink, Banner, NotAllowed, Pill } from '../ui/components.tsx'
import { StepUpField, useSensitive } from '../ui/sensitive.tsx'

interface ExportRow {
  export_id: string; at: string; actor: string | null
  summary: { rows?: number; bytes?: number; tables?: unknown[]; include_pay?: boolean; include_contacts?: boolean; include_incidents?: boolean; include_costs?: boolean; profile?: string }
}
interface BackupStatus { expected: boolean; last_ok_at: string | null; age_hours: number | null; bytes: number | null; git_commit: string | null; last_failure_at: string | null }

/** After this many hours without a good backup the nightly check raises a review item; the page says the same in words. */
const OVERDUE_HOURS = 36

// release b review (finding 15): the vault's one way of saying a size (whole ko, Mo with one decimal; "KB"/"MB" in English)
const size = (bytes: number, lang: string) => sizeText(bytes, lang)

/** The club's data, out (GK-045): one ZIP of CSV files with a manifest, built from a single snapshot, never stored (the server hands it over and forgets it). The manager's and the administrator's, after a fresh second-factor code;
 *  pay rates and staff contacts are in it only for a role that may read them. Every export is recorded: who, when, what it held, never a value.
 *  M16-H: a second profile, the "Dossier durabilité" (docs/M16.md H): through the same path and the same code, a shorter ZIP of five files derived from one snapshot
 *  (water, fuel per machine, inputs, the work tagged habitat and its photos' fingerprints), the registers that decode them and methodology.md; the list says which is which. */
export function ExportsPage() {
  const { t, i18n } = useTranslation()
  const { org, site, deviceId, can } = useClub()
  // M23 item 24: the page is the direction's (export.run); anybody else gets the card, and no RPC is even asked
  const allowed = can('export.run')
  const history = useRpc<ExportRow[]>('export_history', { p_org: org.org_id, p_limit: 20 }, allowed)
  const status = useRpc<BackupStatus>('backup_status', { p_org: org.org_id }, allowed)
  const s = useSensitive()
  const [busy, setBusy] = useState<ExportProfile | null>(null)
  // release b review (finding 10): what was handed over, said under the button that asked for it (the code field's line is a screen above the dossier's card)
  const [done, setDone] = useState<{ profile: ExportProfile; text: string } | null>(null)
  const tz = site.timezone

  async function create(profile: ExportProfile) {
    setBusy(profile); setDone(null)
    try {
      const out = await s.wrap(() => postAdminDownload('/admin/exports', { org_id: org.org_id, device_id: deviceId, profile }))
      if (!out) return
      saveBlob(out.blob, out.filename)
      setDone({ profile, text: t(profile === 'sustainability' ? 'exports.dossier.done' : 'exports.done', { name: out.filename, size: size(out.blob.size, i18n.language) }) })
      void history.reload()
    } finally { setBusy(null) }
  }
  const disabled = s.busy || busy !== null || !s.online

  const b = status.data
  const overdue = b?.expected === true && (b.last_ok_at === null || (b.age_hours ?? 0) > OVERDUE_HOURS)
  if (!allowed) return <NotAllowed permission="export.run" />
  return (
    <section className="flex flex-col gap-5" data-testid="exports-page">
      <BackLink to="/settings">← {t('common.back')}</BackLink>
      <h1 className="text-2xl font-bold">{t('exports.title')}</h1>
      <p className="meta">{t('exports.help')}</p>
      <Banner tone="info">{t('exports.privacy')}</Banner>
      <StepUpField s={s} />
      <button className="btn btn-primary" disabled={disabled} onClick={() => void create('club')} data-testid="exports-create">{busy === 'club' ? t('exports.creating') : t('exports.create')}</button>
      {done?.profile === 'club' && <Banner tone="ok"><span data-testid="exports-done">{done.text}</span></Banner>}

      {/* M16-H: the sustainability dossier, behind the same code (the field above), through the same audited path */}
      <div className="card flex flex-col gap-2" data-testid="exports-dossier-card">
        <h2 className="text-lg font-bold">{t('exports.dossier.title')}</h2>
        <p>{t('exports.dossier.help')}</p>
        <p className="meta">{t('exports.dossier.rule')}</p>
        <button className="btn" disabled={disabled} onClick={() => void create('sustainability')} data-testid="exports-dossier">{busy === 'sustainability' ? t('exports.dossier.creating') : t('exports.dossier.create')}</button>
        {done?.profile === 'sustainability' && <Banner tone="ok"><span data-testid="exports-dossier-done">{done.text}</span></Banner>}
      </div>

      <div className="card flex flex-col gap-2" data-testid="backup-status">
        <h2 className="text-lg font-bold">{t('exports.backup.title')}</h2>
        {!b ? <p className="opacity-70">{status.online ? t('common.loading') : t('exports.offline')}</p> : (
          <>
            {b.last_ok_at
              ? <p data-testid="backup-last">{t('exports.backup.last', { at: formatLocal(b.last_ok_at, tz), hours: fmtNum(b.age_hours ?? 0, i18n.language) })}{b.bytes ? ` · ${size(b.bytes, i18n.language)}` : ''}</p>
              : <p data-testid="backup-last">{t('exports.backup.none')}</p>}
            {!b.expected && <p className="meta" data-testid="backup-not-expected">{t('exports.backup.notExpected')}</p>}
            {overdue && <Banner tone="error"><span data-testid="backup-overdue">{t('exports.backup.overdue', { hours: OVERDUE_HOURS })}</span></Banner>}
            {b.last_failure_at && <p className="meta">{t('exports.backup.failure', { at: formatLocal(b.last_failure_at, tz) })}</p>}
          </>
        )}
        <p className="meta">{t('exports.backup.note')}</p>
      </div>

      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-bold">{t('exports.history')}</h2>
        {history.data && history.data.length === 0 && <p className="opacity-70" data-testid="exports-none">{t('exports.none')}</p>}
        <ul className="flex flex-col gap-2" data-testid="exports-history">
          {(history.data ?? []).map((e) => (
            <li key={e.export_id} className="card flex flex-col gap-1" data-testid="export-row" data-profile={e.summary.profile ?? 'club'}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold">{formatLocal(e.at, tz)}</span>
                <span className="meta">{e.actor ?? t('history.system')}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span>{t('exports.rows', { count: e.summary.rows ?? 0 })}</span>
                <span>· {t('exports.tables', { count: e.summary.tables?.length ?? 0 })}</span>
                {e.summary.bytes ? <span>· {size(e.summary.bytes, i18n.language)}</span> : null}
                {e.summary.include_pay && <Pill tone="amber">{t('exports.withPay')}</Pill>}
                {e.summary.include_contacts && <Pill tone="amber">{t('exports.withContacts')}</Pill>}
                {e.summary.include_incidents && <Pill tone="amber">{t('exports.withIncidents')}</Pill>}
                {e.summary.include_costs && <Pill tone="amber">{t('exports.withCosts')}</Pill>}
                {e.summary.profile === 'sustainability' && <Pill tone="green">{t('exports.profileSustainability')}</Pill>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
